// Race weather: rain intensity over the race (planned in laps, so it scales
// with race length) and the water on the track, which builds quickly under
// rain and dries slowly afterwards. Calibration: docs/research/race-model.md.

export type WeatherPreset = 'dry' | 'wet-start' | 'shower' | 'changeable' | 'storm';

export interface RainCell {
  /** Start and end, in race laps (fractional). */
  from: number;
  to: number;
  /** Peak intensity 0..1 (0.3 drizzle, 0.6 rain, 1 tropical downpour). */
  peak: number;
}

export interface WeatherPlan {
  /** Water on track at the start (0..1). */
  startWetness: number;
  cells: RainCell[];
}

export const WEATHER_LABELS: Record<WeatherPreset, string> = {
  dry: 'Dry',
  'wet-start': 'Wet start, drying',
  shower: 'Passing shower',
  changeable: 'Changeable',
  storm: 'Tropical storm',
};

/** Seeded plan for a preset and race length. */
export function makePlan(preset: WeatherPreset, laps: number, seed: number): WeatherPlan {
  const rand = rng(seed);
  const at = (frac: number): number => Math.max(0.3, frac * laps);
  switch (preset) {
    case 'dry':
      return { startWetness: 0, cells: [] };
    case 'wet-start':
      return { startWetness: 0.45 + rand() * 0.15, cells: [] };
    case 'shower': {
      const from = at(0.3 + rand() * 0.25);
      return { startWetness: 0, cells: [{ from, to: from + Math.max(1, laps * (0.12 + rand() * 0.08)), peak: 0.45 + rand() * 0.25 }] };
    }
    case 'changeable': {
      const a = at(0.15 + rand() * 0.15);
      const b = at(0.55 + rand() * 0.2);
      return {
        startWetness: rand() < 0.4 ? 0.25 : 0,
        cells: [
          { from: a, to: a + Math.max(0.8, laps * 0.1), peak: 0.3 + rand() * 0.2 },
          { from: b, to: b + Math.max(1, laps * 0.15), peak: 0.5 + rand() * 0.3 },
        ],
      };
    }
    case 'storm': {
      const from = at(0.25 + rand() * 0.2);
      return { startWetness: 0, cells: [{ from, to: from + Math.max(1.5, laps * 0.25), peak: 0.85 + rand() * 0.15 }] };
    }
  }
}

/** Rain intensity at a point in the race: smooth onset and fade around each cell. */
export function rainAt(plan: WeatherPlan, lap: number): number {
  let r = 0;
  for (const c of plan.cells) {
    const len = c.to - c.from;
    const ramp = Math.min(0.5, len * 0.3);
    if (lap < c.from - ramp || lap > c.to + ramp) continue;
    const up = smooth(c.from - ramp, c.from + ramp, lap);
    const down = 1 - smooth(c.to - ramp, c.to + ramp, lap);
    r = Math.max(r, c.peak * Math.min(up, down));
  }
  return r;
}

export class Weather {
  rain = 0;
  wetness: number;
  /** Race Director's Low Grip Conditions (B1.5.11): front-wing-only Straight Mode, reduced ERS. */
  lowGrip = false;

  constructor(
    readonly plan: WeatherPlan,
    /** Race-length time scale: weather evolves this much faster per lap in shorter races. */
    readonly scale = 1,
  ) {
    this.wetness = plan.startWetness;
    this.lowGrip = this.wetness > 0.25;
  }

  /** Advance by `dLaps` of race distance, at race position `lap` (fractional). */
  step(lap: number, dLaps: number): void {
    this.rain = rainAt(this.plan, lap);
    const k = dLaps * this.scale;
    const target = this.rain;
    if (this.wetness < target) {
      // Water builds within about a lap of steady rain.
      this.wetness += (target - this.wetness) * Math.min(1, 1.6 * k);
    } else {
      // Drying: standing water drains quickly, the last damp patches take longest.
      // ~0.055 per lap with cars running (Sepang 2026: 0.46 → 0.14 over laps 3–8).
      const rate = 0.055 + 0.12 * Math.max(0, this.wetness - 0.6);
      this.wetness = Math.max(target, this.wetness - rate * k);
    }
    // Declared with hysteresis so it doesn't flicker.
    if (!this.lowGrip && this.wetness > 0.25) this.lowGrip = true;
    else if (this.lowGrip && this.wetness < 0.12) this.lowGrip = false;
  }

  /** Forecast for the coming laps: expected rain per lap, with a little uncertainty in timing. */
  forecast(lap: number, laps: number, seed: number): Array<{ lap: number; rain: number }> {
    const rand = rng(seed + Math.floor(lap));
    const jitter = (rand() - 0.5) * 0.6;
    const out: Array<{ lap: number; rain: number }> = [];
    for (let i = 1; i <= laps; i++) {
      const l = Math.floor(lap) + i;
      out.push({ lap: l, rain: rainAt(this.plan, l - 0.5 + jitter) });
    }
    return out;
  }

  /** Next lap (from `lap`) where rain starts or stops, for the race engineer. */
  nextChange(lap: number, horizon: number): { lap: number; starts: boolean } | undefined {
    const now = rainAt(this.plan, lap) > 0.1;
    for (let l = lap + 0.25; l <= lap + horizon; l += 0.25) {
      const r = rainAt(this.plan, l) > 0.1;
      if (r !== now) return { lap: l, starts: r };
    }
    return undefined;
  }
}

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

export function rng(seed: number): () => number {
  let s = Math.floor(Math.abs(seed)) % 2147483646 || 1;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}
